import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { projectRoi, type RoiResult } from "@/lib/roi-calculator.functions";
import { AnswerBlock } from "@/components/AnswerBlock";
import { FaqAccordion, type FaqItem } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "recharts";

export const Route = createFileRoute("/tools/roi-calculator")({
  head: () => ({
    meta: [
      { title: "Trades Marketing ROI Calculator — Lehi Trades SEO" },
      {
        name: "description",
        content:
          "AI-powered ROI calculator for HVAC, plumbing, electrical, and roofing contractors. Project leads, jobs, and revenue from an SEO + Google Ads program.",
      },
      { property: "og:title", content: "Trades Marketing ROI Calculator" },
      {
        property: "og:description",
        content:
          "Free AI tool projecting 6-month leads, jobs, and revenue from a trades marketing program.",
      },
      { property: "og:url", content: "https://lehitradessaeo.com/tools/roi-calculator" },
    ],
    links: [{ rel: "canonical", href: "https://lehitradessaeo.com/tools/roi-calculator" }],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    trade: typeof search.trade === "string" ? (search.trade as string) : undefined,
    city: typeof search.city === "string" ? (search.city as string) : undefined,
    spend: typeof search.spend === "string" || typeof search.spend === "number" ? Number(search.spend) : undefined,
    avg: typeof search.avg === "string" || typeof search.avg === "number" ? Number(search.avg) : undefined,
    close: typeof search.close === "string" || typeof search.close === "number" ? Number(search.close) : undefined,
    leads: typeof search.leads === "string" || typeof search.leads === "number" ? Number(search.leads) : undefined,
    auto: search.auto === "1" || search.auto === 1 ? 1 : undefined,
  }),
  component: RoiCalculatorPage,
});

function fmtUSD(n: number) {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function RoiTrajectory({
  result,
  form,
}: {
  result: RoiResult;
  form: {
    currentMonthlyLeads: number;
    closeRate: number;
    averageJobValue: number;
    monthlyMarketingSpend: number;
  };
}) {
  const data = buildTrajectory(
    form.currentMonthlyLeads,
    result.projectedLeadsPerMonth,
    form.closeRate,
    form.averageJobValue,
    form.monthlyMarketingSpend,
  );
  return (
    <div className="mt-8 grid gap-6 md:grid-cols-2">
      <div className="rounded-xl border border-border bg-background p-4">
        <h3 className="text-sm font-medium">Lead volume trajectory</h3>
        <ChartContainer config={leadsConfig} className="mt-2 h-48 w-full">
          <AreaChart data={data}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis tickLine={false} axisLine={false} fontSize={11} />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Area
              dataKey="leads"
              type="monotone"
              stroke="var(--color-leads)"
              fill="var(--color-leads)"
              fillOpacity={0.25}
            />
          </AreaChart>
        </ChartContainer>
      </div>
      <div className="rounded-xl border border-border bg-background p-4">
        <h3 className="text-sm font-medium">Revenue ramp</h3>
        <ChartContainer config={revenueConfig} className="mt-2 h-48 w-full">
          <AreaChart data={data}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis
              tickLine={false}
              axisLine={false}
              fontSize={11}
              tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Area
              dataKey="revenue"
              type="monotone"
              stroke="var(--color-revenue)"
              fill="var(--color-revenue)"
              fillOpacity={0.25}
            />
          </AreaChart>
        </ChartContainer>
      </div>
      <div className="rounded-xl border border-border bg-background p-4 md:col-span-2">
        <h3 className="text-sm font-medium">Cost per acquired job</h3>
        <ChartContainer config={cpaConfig} className="mt-2 h-48 w-full">
          <LineChart data={data}>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis dataKey="month" tickLine={false} axisLine={false} fontSize={11} />
            <YAxis
              tickLine={false}
              axisLine={false}
              fontSize={11}
              tickFormatter={(v: number) => `$${v}`}
            />
            <ChartTooltip content={<ChartTooltipContent />} />
            <Line
              dataKey="cpa"
              type="monotone"
              stroke="var(--color-cpa)"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </div>
    </div>
  );
}

/** Linearly interpolate current → projected over 6 months. */
function buildTrajectory(
  currentLeads: number,
  projectedLeads: number,
  closeRatePct: number,
  avgJobValue: number,
  monthlySpend: number,
) {
  const months = 6;
  const out: Array<{
    month: string;
    leads: number;
    revenue: number;
    cpa: number;
  }> = [];
  for (let m = 1; m <= months; m++) {
    const t = m / months;
    const leads = Math.round(currentLeads + (projectedLeads - currentLeads) * t);
    const jobs = leads * (closeRatePct / 100);
    const revenue = Math.round(jobs * avgJobValue);
    const cpa = jobs > 0 ? Math.round(monthlySpend / jobs) : 0;
    out.push({ month: `M${m}`, leads, revenue, cpa });
  }
  return out;
}

const leadsConfig = {
  leads: { label: "Leads / mo", color: "var(--chart-1)" },
} satisfies ChartConfig;
const revenueConfig = {
  revenue: { label: "Revenue / mo", color: "var(--chart-2)" },
} satisfies ChartConfig;
const cpaConfig = {
  cpa: { label: "Cost per acquired job", color: "var(--chart-3)" },
} satisfies ChartConfig;

type Trade = "plumber" | "electrician" | "hvac" | "roofer" | "other";

const TRADE_FAQS: Record<Trade, FaqItem[]> = {
  plumber: [
    { q: "What lead volume should a plumber expect after 6 months?", a: "A focused city-page + GBP + LSA program typically lifts a plumber from 15–25 leads/mo to 50–90 leads/mo by month 6 in Utah County markets." },
    { q: "What is a healthy cost per acquired plumbing job?", a: "$60–$140 per booked job is the realistic target band for residential service plumbers once LSAs and Map Pack are tuned." },
    { q: "How fast does the program pay back for a plumber?", a: "With an average ticket around $450–$900, most plumbers reach payback inside 2–3 months." },
  ],
  electrician: [
    { q: "What lead volume should an electrician expect after 6 months?", a: "Service electricians typically scale to 40–80 qualified leads/mo by month 6; panel-upgrade specialists hit the lower end with higher ticket size." },
    { q: "What is a healthy CPA for an electrician?", a: "$70–$160 per booked job. EV-charger and panel work pulls CPA higher but ticket size more than compensates." },
    { q: "How fast does the program pay back for an electrician?", a: "Average ticket of $600–$2,000 means payback is usually 1.5–3 months." },
  ],
  hvac: [
    { q: "What lead volume should an HVAC contractor expect after 6 months?", a: "30–70 service leads/mo plus 5–15 install opportunities is a realistic 6-month outcome for a tuned HVAC program." },
    { q: "What is a healthy CPA for HVAC?", a: "$80–$200 per booked service call; $400–$900 per install lead is the right band." },
    { q: "How fast does the program pay back for HVAC?", a: "A single install ($6k–$14k) usually pays back 1–2 months of spend. Service-call-only shops see 2–4 months." },
  ],
  roofer: [
    { q: "What lead volume should a roofer expect after 6 months?", a: "20–50 inspection requests/mo is typical, weighted heavier in storm seasons." },
    { q: "What is a healthy CPA for a roofer?", a: "$200–$600 per signed contract. Insurance/storm work runs higher CPA but with much larger ticket size." },
    { q: "How fast does the program pay back for a roofer?", a: "One re-roof ($12k–$25k) usually covers 3–6 months of marketing spend, so payback is fast even with longer sales cycles." },
  ],
  other: [
    { q: "What lead volume should I expect after 6 months?", a: "Most trades programs 2–4× current lead volume by month 6 once city pages, GBP, and paid search are in place." },
    { q: "What is a healthy CPA?", a: "Target a CPA below 10% of your average job value. Plug your numbers above for a trade-specific target." },
    { q: "How fast does the program pay back?", a: "Payback timing depends on average ticket and close rate. The calculator above estimates payback in months for your inputs." },
  ],
};

function buildShareSearch(form: {
  trade: Trade;
  city: string;
  monthlyMarketingSpend: number;
  averageJobValue: number;
  closeRate: number;
  currentMonthlyLeads: number;
}) {
  return {
    trade: form.trade,
    city: form.city,
    spend: form.monthlyMarketingSpend,
    avg: form.averageJobValue,
    close: form.closeRate,
    leads: form.currentMonthlyLeads,
    auto: 1 as const,
  };
}

function RoiCalculatorPage() {
  const calc = useServerFn(projectRoi);
  const search = Route.useSearch();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<RoiResult | null>(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({
    trade: (search.trade as Trade | undefined) ?? ("plumber" as Trade),
    city: search.city ?? "Lehi",
    monthlyMarketingSpend: Number.isFinite(search.spend) ? Number(search.spend) : 2000,
    averageJobValue: Number.isFinite(search.avg) ? Number(search.avg) : 1500,
    closeRate: Number.isFinite(search.close) ? Number(search.close) : 35,
    currentMonthlyLeads: Number.isFinite(search.leads) ? Number(search.leads) : 20,
  });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    setResult(null);
    setCopied(false);
    try {
      const r = await calc({ data: form });
      setResult(r);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Couldn't compute the projection.";
      setErr(msg.includes("402") ? "Free credit limit hit. Try again later." : msg);
    } finally {
      setLoading(false);
    }
  }

  // Auto-run when arriving via a shared link.
  useEffect(() => {
    if (search.auto === 1 && !result && !loading) {
      void onSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams();
    const s = buildShareSearch(form);
    for (const [k, v] of Object.entries(s)) params.set(k, String(v));
    return `${window.location.origin}/tools/roi-calculator?${params.toString()}`;
  }, [form]);

  async function copyShareLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // best-effort
    }
  }

  const faqItems = TRADE_FAQS[form.trade];

  return (
    <main className="mx-auto max-w-3xl px-6 py-16">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Free tool</p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
        Trades Marketing ROI Calculator
      </h1>
      <p className="mt-4 text-lg text-muted-foreground">
        Plug in your numbers. Our AI projects a 6-month outcome for an SEO +
        Google Ads program built around Utah-trades benchmarks.
      </p>

      <AnswerBlock
        question="What ROI should a trades contractor expect from SEO + Google Ads?"
        answer="Most well-run trades marketing programs return 3–7× on monthly spend by month 6 once city-page architecture, GBP, and Local Service Ads are dialed in. Higher-ticket trades (roofing, HVAC installs) hit payback faster than service-call-heavy trades."
      />

      <form
        onSubmit={onSubmit}
        className="mt-8 grid gap-5 rounded-2xl border border-border bg-card p-6"
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium">Trade</span>
            <select
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.trade}
              onChange={(e) => setForm({ ...form, trade: e.target.value as typeof form.trade })}
            >
              <option value="plumber">Plumber</option>
              <option value="electrician">Electrician</option>
              <option value="hvac">HVAC</option>
              <option value="roofer">Roofer</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium">Primary city</span>
            <input
              type="text"
              maxLength={80}
              required
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Monthly marketing spend (USD)</span>
            <input
              type="number"
              min={0}
              max={100000}
              required
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.monthlyMarketingSpend}
              onChange={(e) =>
                setForm({ ...form, monthlyMarketingSpend: Number(e.target.value) })
              }
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Average job value (USD)</span>
            <input
              type="number"
              min={50}
              max={100000}
              required
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.averageJobValue}
              onChange={(e) => setForm({ ...form, averageJobValue: Number(e.target.value) })}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Lead close rate (%)</span>
            <input
              type="number"
              min={1}
              max={100}
              required
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.closeRate}
              onChange={(e) => setForm({ ...form, closeRate: Number(e.target.value) })}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Current leads / month</span>
            <input
              type="number"
              min={0}
              max={5000}
              required
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={form.currentMonthlyLeads}
              onChange={(e) =>
                setForm({ ...form, currentMonthlyLeads: Number(e.target.value) })
              }
            />
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {loading ? "Calculating with AI…" : "Project my 6-month ROI"}
        </button>
        {err && <p className="text-sm text-destructive">{err}</p>}
      </form>

      {result && (
        <section className="mt-10 rounded-2xl border border-primary/30 bg-primary/5 p-6">
          <h2 className="font-display text-2xl tracking-tight">Your 6-month projection</h2>
          <p className="mt-2 text-muted-foreground">{result.summary}</p>
          <dl className="mt-5 grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Projected leads / mo</dt>
              <dd className="font-display text-2xl">{Math.round(result.projectedLeadsPerMonth)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Projected jobs / mo</dt>
              <dd className="font-display text-2xl">{Math.round(result.projectedJobsPerMonth)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Projected revenue / mo</dt>
              <dd className="font-display text-2xl">{fmtUSD(result.projectedRevenuePerMonth)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">ROI multiple</dt>
              <dd className="font-display text-2xl">{result.projectedRoiMultiple.toFixed(1)}×</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Payback</dt>
              <dd className="font-display text-2xl">{result.paybackMonths.toFixed(1)} mo</dd>
            </div>
          </dl>

          <RoiTrajectory result={result} form={form} />

          {result.recommendations.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground">
                Recommendations
              </h3>
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
                {result.recommendations.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          )}

          {result.assumptions.length > 0 && (
            <p className="mt-5 text-xs text-muted-foreground">
              <strong>Assumptions:</strong> {result.assumptions.join(" · ")}
            </p>
          )}

          <Link
            to="/contact"
            className="mt-6 inline-flex rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Get a real audit from our team
          </Link>

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-border/60 pt-5">
            <Button onClick={copyShareLink} variant="outline" size="sm">
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? "Link copied" : "Copy shareable link"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Anyone with the link sees the same projection with your inputs preloaded.
            </p>
          </div>
        </section>
      )}

      <FaqAccordion
        title={`${form.trade === "other" ? "Trades" : form.trade.replace(/^\w/, (c) => c.toUpperCase())} ROI — common questions`}
        items={faqItems}
        pageUrl="https://lehitradessaeo.com/tools/roi-calculator"
      />
    </main>
  );
}